import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Loader2, X } from 'lucide-react'
import { PoolIcon } from '@/lib/entity-icons'
import { apiService } from '@/services/apiService'
import { usePools } from '@/hooks/use-pools'
import { useAccountNames } from '@/hooks/use-accounts'
import type { components } from '../../../../../types/flex-api'

type Pool = components['schemas']['Pool']

type SearchResult = {
  pool: Pool
  matchOn: 'name' | 'poolID' | 'accountID' | 'accountName' | 'tag' | 'system'
  tagKey?: string
  tagValue?: string
  systemID?: string
  accountName?: string
}

function Highlight({ text, query }: { text: string; query: string }) {
  if (!query) return <>{text}</>
  const idx = text.toLowerCase().indexOf(query.toLowerCase())
  if (idx === -1) return <>{text}</>
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-yellow-200 dark:bg-yellow-800 text-inherit rounded-[2px] px-px">{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  )
}

function isUUID(str: string) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str.trim())
}

export default function SearchBar() {
  const [query, setQuery] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [focused, setFocused] = useState(false)
  const [selectedIdx, setSelectedIdx] = useState(-1)
  const [systemResults, setSystemResults] = useState<SearchResult[]>([])
  const [systemSearching, setSystemSearching] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { data: pools = [] } = usePools()
  const accountNames = useAccountNames()

  // "/" keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== '/') return
      const target = e.target as HTMLElement
      const tag = target.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable) return
      e.preventDefault()
      inputRef.current?.focus()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Instant results from already-loaded pools data
  const instantResults: SearchResult[] = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return []

    const results: SearchResult[] = []

    for (const pool of pools) {
      if (results.length >= 8) break

      if (pool.name.toLowerCase().includes(q)) {
        results.push({ pool, matchOn: 'name' })
        continue
      }
      if (pool.poolID.toLowerCase().includes(q)) {
        results.push({ pool, matchOn: 'poolID' })
        continue
      }
      if (pool.accountID.toLowerCase().includes(q)) {
        results.push({ pool, matchOn: 'accountID' })
        continue
      }
      const acctName = accountNames[pool.accountID]
      if (acctName && acctName.toLowerCase().includes(q)) {
        results.push({ pool, matchOn: 'accountName', accountName: acctName })
        continue
      }
      if (pool.tags) {
        for (const [k, v] of Object.entries(pool.tags)) {
          if (k.toLowerCase().includes(q) || v.toLowerCase().includes(q)) {
            results.push({ pool, matchOn: 'tag', tagKey: k, tagValue: v })
            break
          }
        }
      }
    }

    return results
  }, [query, pools, accountNames])

  // Async system UUID lookup — only fires for a complete UUID with no direct pool/account match
  useEffect(() => {
    const q = query.trim()
    if (!isUUID(q)) {
      setSystemResults([])
      setSystemSearching(false)
      return
    }
    // Check directly against pools to avoid depending on instantResults (which
    // would create a render loop via unstable useQueries array references)
    const alreadyMatched = pools.some((p) => p.poolID === q || p.accountID === q)
    if (alreadyMatched) {
      setSystemResults([])
      setSystemSearching(false)
      return
    }

    setSystemSearching(true)
    let cancelled = false

    const timer = setTimeout(async () => {
      try {
        const res = await apiService.systems.getPoolsBySystem(q)
        if (cancelled) return
        if (res.success && res.data && res.data.length > 0) {
          const matched = (res.data as string[])
            .map((pid) => pools.find((p) => p.poolID === pid))
            .filter((p): p is Pool => !!p)
          setSystemResults(matched.map((pool) => ({ pool, matchOn: 'system', systemID: q })))
        } else {
          setSystemResults([])
        }
      } catch {
        setSystemResults([])
      } finally {
        if (!cancelled) setSystemSearching(false)
      }
    }, 350)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [query, pools])

  const allResults = [...instantResults, ...systemResults]
  const showDropdown = focused && query.trim().length > 0

  useEffect(() => {
    setSelectedIdx(-1)
  }, [query])

  const navigateTo = (result: SearchResult) => {
    navigate(`/pools/${result.pool.poolID}`)
    setQuery('')
    setFocused(false)
    inputRef.current?.blur()
  }

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIdx((i) => Math.min(i + 1, allResults.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIdx((i) => Math.max(i - 1, -1))
    } else if (e.key === 'Enter' && selectedIdx >= 0) {
      e.preventDefault()
      navigateTo(allResults[selectedIdx])
    } else if (e.key === 'Escape') {
      setFocused(false)
      inputRef.current?.blur()
    }
  }

  // Full-UUID submit (fallback when no dropdown item is selected)
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (selectedIdx >= 0 && allResults[selectedIdx]) {
      navigateTo(allResults[selectedIdx])
      return
    }

    const searchQuery = query.trim()
    if (!searchQuery || !isUUID(searchQuery)) return

    setSubmitting(true)
    try {
      // Already handled by dropdown for pool/account/system hits in loaded data,
      // but fall back to direct API lookup for anything not in local cache.
      const poolRes = await apiService.pools.getById(searchQuery)
      if (poolRes.success) {
        navigate(`/pools/${searchQuery}`)
        setQuery('')
        return
      }
      const sysRes = await apiService.systems.getPoolsBySystem(searchQuery)
      if (sysRes.success && sysRes.data && (sysRes.data as string[]).length > 0) {
        navigate(`/pools/${(sysRes.data as string[])[0]}`)
        setQuery('')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="relative">
      <form onSubmit={handleSubmit} className="relative flex items-center">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none z-10" />
        <input
          ref={inputRef}
          type="text"
          placeholder={focused ? 'Search by Pool ID or System ID…' : 'Search…'}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          onKeyDown={handleInputKeyDown}
          disabled={submitting}
          className="flex h-9 w-64 rounded-md border border-input bg-muted/50 pl-9 pr-10 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:bg-background disabled:cursor-not-allowed disabled:opacity-50 transition-[width,background-color] duration-200 focus:w-96"
        />
        {query && !submitting && (
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); setQuery('') }}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 hover:bg-muted rounded transition-colors"
            aria-label="Clear search"
          >
            <X className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
          </button>
        )}
        {submitting ? (
          <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
        ) : (
          !focused && !query && (
            <kbd className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none hidden sm:inline-flex h-5 select-none items-center rounded border border-border bg-background px-1.5 font-mono text-[10px] font-medium text-muted-foreground shadow-sm">
              /
            </kbd>
          )
        )}
      </form>

      {/* Dropdown */}
      {showDropdown && (
        <div
          ref={dropdownRef}
          className="absolute right-0 top-full mt-1 w-[28rem] rounded-md border border-border bg-popover shadow-lg z-50 overflow-hidden"
        >
          {allResults.length === 0 && !systemSearching ? (
            <p className="px-4 py-3 text-sm text-muted-foreground">No results found</p>
          ) : (
            <ul>
              {allResults.map((result, i) => (
                <li key={`${result.pool.poolID}-${result.matchOn}`}>
                  <button
                    type="button"
                    onMouseDown={() => navigateTo(result)}
                    className={`w-full flex items-start gap-3 px-4 py-2.5 text-left transition-colors ${
                      i === selectedIdx ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'
                    }`}
                  >
                    <PoolIcon className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">
                        <Highlight text={result.pool.name} query={result.matchOn === 'name' ? query.trim() : ''} />
                      </div>
                      <div className="text-xs text-muted-foreground font-mono truncate mt-0.5">
                        {result.matchOn === 'poolID' ? (
                          <Highlight text={result.pool.poolID} query={query.trim()} />
                        ) : (
                          result.pool.poolID
                        )}
                      </div>
                      {result.matchOn === 'accountID' && (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          Account: <span className="font-mono"><Highlight text={result.pool.accountID} query={query.trim()} /></span>
                        </div>
                      )}
                      {result.matchOn === 'accountName' && result.accountName && (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          Account: <Highlight text={result.accountName} query={query.trim()} />
                        </div>
                      )}
                      {result.matchOn === 'tag' && result.tagKey && (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          Tag: <span className="font-mono"><Highlight text={`${result.tagKey}=${result.tagValue}`} query={query.trim()} /></span>
                        </div>
                      )}
                      {result.matchOn === 'system' && result.systemID && (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          Contains system <span className="font-mono"><Highlight text={result.systemID} query={query.trim()} /></span>
                        </div>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {systemSearching && (
            <div className="flex items-center gap-2 px-4 py-2 border-t border-border text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              Searching systems…
            </div>
          )}
        </div>
      )}
    </div>
  )
}
